import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
    SortingState,
    Updater,
} from '@tanstack/react-table';
import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchAllSupporters, exportAllSupporters } from '~/services/campaign';
import { toast } from 'react-hot-toast';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { IDonors } from '~/types/campaigns';
import { MdLock } from 'react-icons/md';
import { FiEye } from 'react-icons/fi';
import moment from 'moment';
import { BiDownload } from 'react-icons/bi';
import { FaSync } from 'react-icons/fa';
import { useIsFetching, useQuery } from '@tanstack/react-query';
import { useCacheInvalidation } from '~/utils/cacheInvalidation';
import { formatCurrency } from '~/common/form/Inputs';
import { buildStripePaymentUrl, isStripeLookupId } from '~/common/stripeUrl';
import { formatPaymentSource } from '~/common/paymentSource';
import { Pagination } from '~/components/Pagination';
import DonationDetailDrawer, {
    DonationDetail,
} from '~/components/Contributions/drawers/DonationDetailDrawer';
import RefundBadge from '~/components/Contributions/RefundBadge';
import AllRefundsTable from '~/components/Contributions/AllRefundsTable';

type DonorRow = IDonors & {
    action?: string;
    campaign_id?: number | null;
    campaign_name?: string;
    raw: DonationDetail;
};

const columnHelper = createColumnHelper<DonorRow>();

type DateRange = {
    start_date: string;
    end_date: string;
};

const initialDateRange: DateRange = {
    start_date: '',
    end_date: '',
};

const PAGE_SIZE = 20;

const OneTimeContributions: React.FC = () => {
    const { refreshOneTimeContributions } = useCacheInvalidation();
    const [page, setPage] = useState<number>(1);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [dateRange, setDateRange] = useState<DateRange>(initialDateRange);
    const [donationType, setDonationType] = useState<string>('');
    const [paymentMethod, setPaymentMethod] = useState<string>('');
    // 'contributions' is the donation list; 'refunds' swaps the table for every
    // refund, pending or processed, which the contribution rows can't show on
    // their own — a full refund deletes the donation row (HHLTB-2630).
    const [view, setView] = useState<'contributions' | 'refunds'>(
        'contributions'
    );
    const [refundStatus, setRefundStatus] = useState<string>('');
    const [searchInput, setSearchInput] = useState<string>('');
    const [search, setSearch] = useState<string>('');
    const [ntafIdInput, setNtafIdInput] = useState<string>('');
    const [ntafId, setNtafId] = useState<string>('');
    const [activeDonation, setActiveDonation] = useState<DonationDetail | null>(
        null
    );
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const isRefundView = view === 'refunds';

    useEffect(() => {
        const handle = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(handle);
    }, [searchInput]);

    useEffect(() => {
        const handle = setTimeout(() => {
            setNtafId(ntafIdInput.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(handle);
    }, [ntafIdInput]);

    const hasDateRange = !!(dateRange.start_date && dateRange.end_date);

    // Sorting is the server's job so it covers every match, not just the page
    // in hand — staff hunting one $1 tip across a month shouldn't have to page
    // through the whole range (HHLTB-2604).
    const sortParams = useMemo(() => {
        const [first] = sorting;
        if (!first) return {};
        return { sort_by: first.id, sort_dir: first.desc ? 'desc' : 'asc' };
    }, [sorting]);

    // A new sort reorders the whole result set, so the old page number is
    // meaningless — go back to the first page of the new order.
    const handleSortingChange = (updater: Updater<SortingState>) => {
        setPage(1);
        setSorting(updater);
    };

    const { data: donorResults, isLoading } = useQuery({
        queryKey: [
            'all-supporters',
            page,
            dateRange,
            donationType,
            paymentMethod,
            search,
            ntafId,
            sortParams,
        ],
        queryFn: () =>
            fetchAllSupporters(page, {
                from: dateRange.start_date,
                to: dateRange.end_date,
                donation_type: donationType || undefined,
                payment_method: paymentMethod || undefined,
                search: search || undefined,
                ntaf_id: ntafId || undefined,
                ...sortParams,
            }),
        enabled: !isRefundView,
        staleTime: Infinity,
    });

    // The refunds list is fetched inside AllRefundsTable, so the Refresh
    // button watches whichever view's query is on screen rather than the
    // contributions query above, which sits idle in the refunds view.
    const isRefreshing =
        useIsFetching({
            queryKey: [isRefundView ? 'all-refunds' : 'all-supporters'],
        }) > 0;

    // Same filter inputs, different endpoint: the date range here is the refund
    // date, and type/source don't apply.
    const refundFilters = useMemo(
        () => ({
            from: dateRange.start_date || undefined,
            to: dateRange.end_date || undefined,
            search: search || undefined,
            ntaf_id: ntafId || undefined,
            status: refundStatus || undefined,
        }),
        [dateRange.start_date, dateRange.end_date, search, ntafId, refundStatus]
    );

    const handleExport = async () => {
        if (isExporting) return;
        if (!hasDateRange) {
            toast.error('Select a date range before exporting.');
            return;
        }
        setIsExporting(true);
        try {
            const blob = await exportAllSupporters({
                from: dateRange.start_date,
                to: dateRange.end_date,
                donation_type: donationType || undefined,
                payment_method: paymentMethod || undefined,
                search: search || undefined,
                ntaf_id: ntafId || undefined,
                ...sortParams,
            });
            const filename = `one-time-contributions_${dateRange.start_date}_${dateRange.end_date}.csv`;
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(downloadUrl);
        } catch (error: any) {
            console.error('Error exporting supporters:', error);
            toast.error(error?.message || 'Failed to export contributions');
        } finally {
            setIsExporting(false);
        }
    };

    const pages = useMemo(() => {
        if (!donorResults?.count) return 1;
        return Math.ceil(donorResults.count / PAGE_SIZE);
    }, [donorResults?.count]);

    const supporters = useMemo<DonorRow[]>(() => {
        if (!donorResults?.results) return [];
        return donorResults.results.map((donor: any) => {
            const first_name = donor?.name?.split(' ')[0] || '';
            const last_name = donor?.name?.split(' ')[1] || '';
            return {
                first_name,
                last_name,
                gift_date: donor.gift_date,
                contribution: parseFloat(donor.gift_amount) || 0,
                fees_covered: donor.covered_fees,
                email: donor.email,
                address: [
                    donor.address_1,
                    donor.city_state && donor.state
                        ? `${donor.city_state}, ${donor.state}`
                        : donor.city_state || donor.state,
                    donor.zip_code,
                ]
                    .filter(Boolean)
                    .join(', '),
                stripe_charge_id: donor.stripe_charge_id || '',
                payment_method: donor.payment_method || '',
                donor_share_amount: donor.donor_share_amount,
                donation_type: donor.donation_type || '',
                gift_message: donor.gift_message || '',
                campaign_id: donor.campaign_id ?? null,
                campaign_name: donor.campaign_name || '',
                raw: {
                    id: donor.id,
                    name: donor.name,
                    first_name,
                    last_name,
                    gift_date: donor.gift_date,
                    gift_amount: donor.gift_amount,
                    donation_type: donor.donation_type,
                    payment_method: donor.payment_method,
                    covered_fees: donor.covered_fees,
                    donor_share_amount: donor.donor_share_amount,
                    donor_is_anonymous: donor.donor_is_anonymous,
                    gift_message: donor.gift_message,
                    email: donor.email,
                    address_1: donor.address_1,
                    address_2: donor.address_2,
                    city_state: donor.city_state,
                    state: donor.state,
                    zip_code: donor.zip_code,
                    country: donor.country,
                    stripe_charge_id: donor.stripe_charge_id,
                    payout_id: donor.payout_id || '',
                    gift_id: donor.gift_id,
                    lookup_id: donor.lookup_id,
                    ntaf_id: donor.ntaf_id,
                    gift_code: donor.gift_code,
                    campaign_id: donor.campaign_id ?? null,
                    campaign_name: donor.campaign_name || '',
                    created_date: donor.created_date,
                    date_sync: donor.date_sync,
                    refund_summary: donor.refund_summary,
                    event_name: donor.event_name,
                    mip_locked: donor.mip_locked,
                    mip_synced_at: donor.mip_synced_at,
                },
            };
        });
    }, [donorResults?.results]);

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'contributor',
                header: () => 'Contributor',
                cell: (props) => {
                    const { first_name, last_name } = props.row.original;
                    const name = [first_name, last_name]
                        .filter(Boolean)
                        .join(' ');
                    const isAnonymous =
                        !!props.row.original.raw?.donor_is_anonymous;
                    return (
                        <div className='flex flex-col'>
                            <span>{name}</span>
                            {isAnonymous && (
                                <span
                                    className='mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400'
                                    title='Donor chose to stay anonymous — name is hidden on public and client-facing views'
                                >
                                    <MdLock size={10} />
                                    Anonymous
                                </span>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('gift_date', {
                header: () => 'Date',
                cell: (props) => {
                    const createdDate = props.row.original.raw.created_date;
                    const dateSource = createdDate || props.getValue();
                    return (
                        <div className='flex flex-col'>
                            <span>
                                {moment(dateSource).format('MM/DD/YYYY')}
                            </span>
                            {createdDate && (
                                <span className='text-[12px]/[16px] text-[#949599]'>
                                    {moment(createdDate).format('h:mm A')}
                                </span>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('donation_type', {
                header: () => 'Type',
                cell: (props) => {
                    const value = props.getValue();
                    if (!value) return '';
                    return value.charAt(0).toUpperCase() + value.slice(1);
                },
            }),
            columnHelper.accessor('campaign_name', {
                header: () => 'Campaign',
                // Donation rows and campaigns live in separate databases, so
                // there's no column to order the full result set by.
                enableSorting: false,
                cell: (props) => {
                    const name = props.getValue();
                    const id = props.row.original.campaign_id;
                    const ntaf = props.row.original.raw.ntaf_id;
                    const displayName =
                        ntaf === 'NTAF' ? 'General Operating Fund' : name;
                    return (
                        <div className='flex flex-col'>
                            {displayName ? (
                                id ? (
                                    <Link
                                        to={`/campaigns/${id}`}
                                        className='font-bold text-primary'
                                    >
                                        {displayName}
                                    </Link>
                                ) : (
                                    <span>{displayName}</span>
                                )
                            ) : null}
                            {ntaf && (
                                <span className='text-[12px]/[16px] text-[#949599]'>
                                    {ntaf}
                                </span>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('contribution', {
                header: () => 'Contribution',
                cell: (props) => {
                    const isPrivate = !props.row.original.donor_share_amount;
                    return (
                        <div className='flex flex-col items-start'>
                            <span className='whitespace-nowrap'>
                                {formatCurrency(props.getValue())}
                            </span>
                            <RefundBadge
                                summary={props.row.original.raw.refund_summary}
                            />
                            {isPrivate && (
                                <span
                                    className='mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400'
                                    title='Donor opted not to share the amount — hidden on public and client-facing views'
                                >
                                    <MdLock size={10} />
                                    Hidden
                                </span>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('payment_method', {
                header: () => 'Payment Source',
                cell: (props) => {
                    const method = props.getValue();
                    const chargeId = props.row.original.stripe_charge_id;
                    const label = formatPaymentSource(method);
                    if (!chargeId) return label;
                    return (
                        <a
                            href={buildStripePaymentUrl(chargeId) || '#'}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='font-bold text-primary'
                        >
                            {label}
                        </a>
                    );
                },
            }),
            columnHelper.display({
                id: 'status',
                header: () => 'Status',
                cell: (props) => {
                    // Status only applies to Stripe transactions; their lookup_id
                    // is the Stripe payment intent or charge id.
                    // Legacy/manually-entered records use other id formats.
                    const lookupId = props.row.original.raw.lookup_id || '';
                    if (!isStripeLookupId(lookupId)) return null;
                    const isApproved = props.row.original.raw.gift_id != null;
                    const stripeUrl = buildStripePaymentUrl(lookupId);
                    return (
                        <div className='flex flex-col items-start gap-1'>
                            {isApproved ? (
                                <span
                                    className='whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold'
                                    style={{ backgroundColor: '#DCFCE7', color: '#15803D' }}
                                >
                                    Approved
                                </span>
                            ) : (
                                <span
                                    className='whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold'
                                    style={{ backgroundColor: '#FEF3C7', color: '#B45309' }}
                                >
                                    Pending Settlement
                                </span>
                            )}
                            {stripeUrl && (
                                <a
                                    href={stripeUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='px-2 text-xs font-medium text-primary hover:underline'
                                >
                                    View in Stripe
                                </a>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.display({
                id: 'detail',
                header: () => 'Detail',
                cell: (props) => (
                    <button
                        type='button'
                        onClick={() => setActiveDonation(props.row.original.raw)}
                        className='text-primary hover:text-opacity-80'
                        title='View donation detail'
                    >
                        <FiEye size={20} />
                    </button>
                ),
            }),
        ],
        []
    );

    const table = useReactTable({
        data: supporters,
        columns,
        state: { sorting },
        onSortingChange: handleSortingChange,
        getCoreRowModel: getCoreRowModel(),
        // No getSortedRowModel: re-sorting the fetched page on top of the
        // server's ordering is exactly the bug — it looks like a sort but only
        // shuffles twenty rows.
        manualSorting: true,
    });

    const [nowTick, setNowTick] = useState(() => Date.now());
    useEffect(() => {
        const interval = setInterval(() => setNowTick(Date.now()), 60_000);
        return () => clearInterval(interval);
    }, []);

    const lastDonationAt = useMemo(() => {
        if (!donorResults?.results?.length) return null;
        let max: moment.Moment | null = null;
        for (const donor of donorResults.results) {
            if (!donor?.created_date) continue;
            const m = moment(donor.created_date);
            if (!m.isValid()) continue;
            if (!max || m.isAfter(max)) max = m;
        }
        return max;
    }, [donorResults?.results]);

    const lastDonationLabel = useMemo(() => {
        if (!lastDonationAt) return null;
        return lastDonationAt.from(moment(nowTick));
    }, [lastDonationAt, nowTick]);

    const exportDisabled = isExporting || !hasDateRange || !donorResults?.count;
    const exportTitle = !hasDateRange
        ? 'Select a date range to enable export'
        : undefined;

    return (
        <>
            <Breadcrumb pageName='One Time Contributions' />

            <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div className='flex items-center justify-between gap-4 px-8 pb-4'>
                    <div className='flex flex-wrap items-center gap-3'>
                        {/* First in the row on purpose: it is the control that
                            changes which of the others are there, so it can't be
                            one that moves when they swap out. A segmented toggle
                            rather than a select: as a dropdown among dropdowns it
                            read as one more filter value, and "Refunds" stayed
                            hidden until you opened it, so nobody found it. */}
                        <div
                            role='tablist'
                            aria-label='Contributions or refunds'
                            className='inline-flex overflow-hidden rounded border border-stroke dark:border-strokedark'
                        >
                            {(
                                [
                                    ['contributions', 'Contributions'],
                                    ['refunds', 'Refunds'],
                                ] as const
                            ).map(([value, label]) => (
                                <button
                                    key={value}
                                    type='button'
                                    role='tab'
                                    aria-selected={view === value}
                                    className={`px-4 py-2 text-sm font-medium transition ${
                                        view === value
                                            ? 'bg-primary text-white'
                                            : 'bg-white text-black hover:bg-gray-100 dark:bg-boxdark dark:text-white dark:hover:bg-meta-4'
                                    }`}
                                    onClick={() => {
                                        if (view === value) return;
                                        setPage(1);
                                        setView(value);
                                    }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <input
                            type='search'
                            name='search'
                            placeholder='Search name or email'
                            className='rounded border border-stroke p-[6px] px-3 py-2 outline-none focus:border-primary'
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                        />
                        <input
                            type='search'
                            name='ntaf_id'
                            placeholder='NTAF ID'
                            className='w-[120px] rounded border border-stroke p-[6px] px-3 py-2 outline-none focus:border-primary'
                            value={ntafIdInput}
                            onChange={(event) => setNtafIdInput(event.target.value)}
                        />
                        {/* The range means gift date on contributions and refund
                            date on refunds, so it's labelled rather than bare.
                            Fixed width so the shorter label doesn't drag the
                            date inputs sideways on the way in. */}
                        <span className='w-[64px] text-[12px] uppercase tracking-wide text-[#949599]'>
                            {isRefundView ? 'Refunded' : 'Gift date'}
                        </span>
                        <input
                            type='date'
                            name='start_date'
                            className='rounded border border-stroke p-[6px] px-3 py-2 outline-none focus:border-primary'
                            value={dateRange.start_date}
                            onChange={(event) => {
                                setPage(1);
                                setDateRange({
                                    ...dateRange,
                                    start_date: event.target.value,
                                });
                            }}
                        />
                        to
                        <input
                            type='date'
                            name='end_date'
                            className='rounded border border-stroke p-[6px] px-3 py-2 outline-none focus:border-primary'
                            value={dateRange.end_date}
                            onChange={(event) => {
                                setPage(1);
                                setDateRange({
                                    ...dateRange,
                                    end_date: event.target.value,
                                });
                            }}
                        />
                        {/* Type and payment source are donation fields the
                            refunds endpoint can't filter on, and refund status
                            means nothing to a donation — so the two sets swap
                            rather than sit greyed out. They used to be rendered
                            disabled, which read as a filter that had broken
                            (HHLTB-2654). */}
                        {isRefundView ? (
                            <select
                                name='refund_status'
                                className='rounded border border-stroke bg-white p-[6px] px-3 py-2 outline-none focus:border-primary'
                                value={refundStatus}
                                onChange={(event) => {
                                    setPage(1);
                                    setRefundStatus(event.target.value);
                                }}
                            >
                                <option value=''>All Refunds</option>
                                <option value='succeeded'>Processed</option>
                                <option value='pending'>Pending</option>
                                <option value='failed'>Failed</option>
                            </select>
                        ) : (
                            <>
                                <select
                                    name='donation_type'
                                    className='rounded border border-stroke bg-white p-[6px] px-3 py-2 outline-none focus:border-primary'
                                    value={donationType}
                                    onChange={(event) => {
                                        setPage(1);
                                        setDonationType(event.target.value);
                                    }}
                                >
                                    <option value=''>All Types</option>
                                    <option value='donation'>Donation</option>
                                    <option value='event'>Event</option>
                                </select>
                                <select
                                    name='payment_method'
                                    className='rounded border border-stroke bg-white p-[6px] px-3 py-2 outline-none focus:border-primary'
                                    value={paymentMethod}
                                    onChange={(event) => {
                                        setPage(1);
                                        setPaymentMethod(event.target.value);
                                    }}
                                >
                                    <option value=''>All Sources</option>
                                    <option value='credit_card'>
                                        Credit Card
                                    </option>
                                    <option value='ach'>ACH</option>
                                    <option value='apple_pay'>Apple Pay</option>
                                    <option value='google_pay'>
                                        Google Pay
                                    </option>
                                </select>
                            </>
                        )}
                    </div>

                    <div className='flex items-center gap-3'>
                        <button
                            type='button'
                            onClick={() => refreshOneTimeContributions()}
                            className={`inline-flex shrink-0 items-center justify-center rounded p-2 text-gray-600 transition-colors duration-300 hover:bg-gray-50 ${
                                isRefreshing ? 'cursor-wait opacity-60' : ''
                            }`}
                            title='Refresh data'
                            disabled={isRefreshing}
                            aria-busy={isRefreshing}
                        >
                            <FaSync
                                className={`text-base ${
                                    isRefreshing ? 'animate-spin' : ''
                                }`}
                            />
                        </button>
                        {/* Export covers the contributions list only; the refunds
                            view links out to the audit report for its CSV. */}
                        {!isRefundView && (
                            <button
                                type='button'
                                onClick={handleExport}
                                disabled={exportDisabled}
                                title={exportTitle}
                                className='inline-flex shrink-0 items-center justify-center gap-2 rounded-full border-[2px] border-black px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-current'
                            >
                                <BiDownload />
                                {isExporting ? 'Exporting...' : 'Export'}
                            </button>
                        )}
                    </div>
                </div>

                {isRefundView ? (
                    <AllRefundsTable
                        filters={refundFilters}
                        onOpenDonation={setActiveDonation}
                    />
                ) : (
                    <>
                <div
                    className='flex items-center justify-between border-t border-stroke px-8 py-1'
                    style={{ fontSize: '12px' }}
                >
                    <p>
                        Showing {Math.min(PAGE_SIZE, (donorResults?.count || 0) - (page - 1) * PAGE_SIZE)} of {donorResults?.count || 0} results
                    </p>
                    {lastDonationLabel && (
                        <p className='text-[#949599]'>
                            Last donation: {lastDonationLabel}
                        </p>
                    )}
                </div>

                <div className='overflow-x-auto'>
                    <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                        <thead className='border-separate px-4'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    className='border-t border-stroke bg-whiten'
                                    style={{ fontSize: '14px' }}
                                    key={headerGroup.id}
                                >
                                    {headerGroup.headers.map((header) => {
                                        const canSort =
                                            header.column.getCanSort();
                                        return (
                                            <th
                                                style={{
                                                    paddingTop: '16px',
                                                    paddingBottom: '16px',
                                                }}
                                                key={header.id}
                                            >
                                                <div
                                                    className={`flex select-none items-center gap-1 ${
                                                        canSort
                                                            ? 'cursor-pointer'
                                                            : ''
                                                    }`}
                                                    onClick={
                                                        canSort
                                                            ? header.column.getToggleSortingHandler()
                                                            : undefined
                                                    }
                                                >
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column
                                                                  .columnDef
                                                                  .header,
                                                              header.getContext()
                                                          )}
                                                    {{
                                                        asc: ' ▲',
                                                        desc: ' ▼',
                                                    }[
                                                        header.column.getIsSorted() as string
                                                    ] ?? ''}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {isLoading
                                ? Array.from({ length: 10 }).map((_, index) => (
                                      <tr key={index}>
                                          {columns.map((_column, colIndex) => (
                                              <td
                                                  key={colIndex}
                                                  className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                                              >
                                                  <div className='max-w-sm animate-pulse'>
                                                      <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                                  </div>
                                              </td>
                                          ))}
                                      </tr>
                                  ))
                                : table.getRowModel().rows.map((row) => {
                                      return (
                                          <tr
                                              className='border-t border-stroke'
                                              key={row.id}
                                          >
                                              {row
                                                  .getVisibleCells()
                                                  .map((cell) => (
                                                      <td
                                                          style={{
                                                              paddingTop: '16px',
                                                              paddingBottom: '16px',
                                                          }}
                                                          key={cell.id}
                                                      >
                                                          {flexRender(
                                                              cell.column
                                                                  .columnDef
                                                                  .cell,
                                                              cell.getContext()
                                                          )}
                                                      </td>
                                                  ))}
                                          </tr>
                                      );
                                  })}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    page={page}
                    setPage={setPage}
                    pages={pages}
                    totalCount={donorResults?.count}
                />
                    </>
                )}
            </section>

            <DonationDetailDrawer
                isOpen={activeDonation !== null}
                setIsOpen={(open) => {
                    if (!open) setActiveDonation(null);
                }}
                donation={activeDonation}
            />

        </>
    );
};

export default OneTimeContributions;
